import { useState } from "react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Sparkles, Loader2, FileDown, CheckCircle2, FileQuestion } from "lucide-react";
import { exportDoc } from "../../pages/ExamSessionPage";

export default function QcmTab({ api, session, cert, onChange }) {
  const [generating, setGenerating] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const qcm = session.qcm_genere;
  const iaEnabled = !!cert.qcm?.generation_ia;

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/certif/sessions/${session.session_id}/generate-qcm`);
      toast.success("QCM généré — relisez avant validation");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    finally { setGenerating(false); }
  };

  const validate = async () => {
    await api.put(`/certif/sessions/${session.session_id}/qcm-status`, { status: "valide" });
    toast.success("QCM validé"); onChange();
  };

  if (!iaEnabled && !qcm) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-8 text-center" data-testid="qcm-fixed-notice">
        <FileQuestion className="w-8 h-8 text-slate-600 mx-auto mb-2" />
        <p className="text-sm text-slate-400">La génération IA du QCM n'est pas activée pour cette certification.</p>
        <p className="text-xs text-slate-500 mt-1">Le QCM officiel est géré comme document fixe (enveloppes scellées). Vous pouvez activer la génération IA dans la fiche de la certification.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="qcm-toolbar">
        <div className="flex-1">
          <p className="text-sm font-bold text-white">Épreuve 1 — QCM ({cert.qcm?.nb_questions || 40} questions · {cert.qcm?.duree_min || 40} min · seuil {cert.qcm?.seuil || 10}/{cert.qcm?.bareme || 20})</p>
          {qcm && <p className="text-[10px] text-slate-500">Généré le {(qcm.generated_at || "").slice(0, 16).replace("T", " ")} · {qcm.questions?.length} questions · {qcm.status === "valide" ? "VALIDÉ" : "brouillon"}</p>}
        </div>
        <Button onClick={generate} disabled={generating || session.statut === "cloturee"} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="generate-qcm-btn">
          {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          {generating ? "Génération (30-60s)…" : qcm ? "Régénérer le QCM" : "Générer le QCM par IA"}
        </Button>
        {qcm && qcm.status !== "valide" && (
          <Button onClick={validate} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="validate-qcm-btn">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Valider
          </Button>
        )}
        {qcm && (
          <>
            <Button variant="outline" className="border-white/10 text-slate-300" onClick={() => exportDoc(api, session.session_id, "qcm.docx")} data-testid="export-qcm-btn">
              <FileDown className="w-4 h-4 mr-1" /> QCM (DOCX)
            </Button>
            <Button variant="outline" className="border-white/10 text-slate-300" onClick={() => exportDoc(api, session.session_id, "qcm-corrige.docx")} data-testid="export-corrige-btn">
              <FileDown className="w-4 h-4 mr-1" /> Corrigé (DOCX)
            </Button>
          </>
        )}
      </div>

      {qcm && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="qcm-questions">
          <label className="flex items-center gap-2 text-xs text-slate-400 mb-3 cursor-pointer">
            <input type="checkbox" checked={showAnswers} onChange={e => setShowAnswers(e.target.checked)} data-testid="qcm-show-answers-toggle" />
            Afficher les bonnes réponses (mode corrigé)
          </label>
          {(qcm.questions || []).map(q => (
            <div key={q.n} className="py-3 border-t border-white/5" data-testid={`qcm-question-${q.n}`}>
              <p className="text-sm text-white font-medium">
                <span className="text-[#f7c935] font-bold">Q{q.n}.</span> {q.question}
                {q.bloc && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-500">{q.bloc}</span>}
              </p>
              <div className="grid md:grid-cols-2 gap-1 mt-1.5">
                {["A", "B", "C", "D"].map(letter => q.choices?.[letter] && (
                  <p key={letter} className={`text-xs px-2 py-1 rounded ${showAnswers && q.answer === letter ? "bg-emerald-500/15 text-emerald-300 font-bold" : "text-slate-400"}`}>
                    ☐ {letter}. {q.choices[letter]}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
